// Theme for the chart library. Apex draws its own SVG text, so the design tokens (text, muted text, grid,
// surface) are read and handed to it, and charts redraw when the colour mode or preset changes.

import { contrastRatio, luminance, mixHex } from './chart-colors';

export interface ChartTheme {
    mode: 'light' | 'dark';
    /** Axis labels, legend text and data labels. */
    foreColor: string;
    /** Secondary text, e.g. axis titles. */
    mutedColor: string;
    grid: string;
    /** The surface the chart sits on. */
    surface: string;
    fontFamily: string;
}

// Tokens are stored as raw HSL triples (e.g. `198 58% 43%`) so they compose with `hsl(var(--chart-1))` in
// CSS. Apex needs the actual colour, so wrap loose triples and let the browser resolve them into sRGB
// (Apex 3's parser cannot interpret modern CSS colours such as OKLCH).
const resolvedColors = new Map<string, string>();
export function toCssColor(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toLowerCase();
    if (resolvedColors.has(trimmed)) return resolvedColors.get(trimmed)!;
    const css = /^(#|rgb|hsl|oklch|oklab|lab|lch|color)/i.test(trimmed) ? trimmed : `hsl(${trimmed})`;
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') return hslChannelsToHex(trimmed) ?? css;
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = 1;
    const context = canvas.getContext?.('2d'); if (!context) return hslChannelsToHex(trimmed) ?? css;
    // Reset the fill first: an unparseable colour leaves fillStyle unchanged, and must read back as no colour.
    context.clearRect(0, 0, 1, 1); context.fillStyle = 'transparent'; context.fillStyle = css; context.fillRect(0, 0, 1, 1);
    const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
    const hex = '#' + [r, g, b].map(channel => channel.toString(16).padStart(2, '0')).join('');
    if (resolvedColors.size >= 256) resolvedColors.clear();
    resolvedColors.set(trimmed, hex); return hex;
}

function rootStyle(): CSSStyleDeclaration | null {
    if (typeof document === 'undefined' || !document.documentElement || typeof getComputedStyle !== 'function') return null;
    try { return getComputedStyle(document.documentElement); } catch { return null; }
}

export function cssToken(name: string): string {
    return rootStyle()?.getPropertyValue(name).trim() ?? '';
}

function prefersDark(): boolean {
    try { return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
}

/** The colour mode the page asks for: data-color-mode wins, then the operating system preference. */
export function colorMode(): 'light' | 'dark' {
    const attr = typeof document !== 'undefined' ? document.documentElement?.getAttribute?.('data-color-mode') : null;
    if (attr === 'dark' || attr === 'light') return attr;
    return prefersDark() ? 'dark' : 'light';
}

/**
 * The token contract's own defaults (the `:root` block of Platform.DesignSystem/ClientApp/src/tokens.css), as
 * HSL channels. The app always defines these tokens, so in a page they never paint; they are what a chart reads
 * where no stylesheet is loaded (unit tests, a host detached from the document), so every chart colour still
 * comes from the token contract rather than from a colour chosen here.
 */
const TOKEN_CONTRACT_DEFAULTS: Readonly<Record<string, string>> = {
    '--background': '0 0% 100%',
    '--card': '0 0% 100%',
    '--foreground': '222.2 47.4% 11.2%',
    '--muted-foreground': '215.4 16.3% 46.9%',
    '--border': '214.3 31.8% 91.4%',
    '--primary': '222.2 47.4% 11.2%',
    '--primary-foreground': '210 40% 98%',
    '--destructive': '0 84.2% 60.2%',
    '--success': '142 71% 45%',
};

/** `h s% l%` channels (the token storage format) as hex, without a browser; null for anything else. */
function hslChannelsToHex(raw: string): string | null {
    const match = /^(?:hsl\(\s*)?(-?[\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%\s*\)?$/i.exec(raw.trim());
    if (!match) return null;
    const h = ((Number(match[1]) % 360) + 360) % 360, s = Math.min(100, Number(match[2])) / 100, l = Math.min(100, Number(match[3])) / 100;
    const a = s * Math.min(l, 1 - l);
    const channel = (n: number) => { const k = (n + h / 30) % 12; return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)))); };
    return '#' + [channel(0), channel(8), channel(4)].map(v => v.toString(16).padStart(2, '0')).join('');
}

/**
 * A design token as a concrete hex colour for the chart library: the first of `names` the page defines, else
 * the token contract's default for it.
 */
export function themeColor(...names: string[]): string {
    for (const name of names) {
        const value = cssToken(name);
        const color = value ? toCssColor(value) : '';
        if (/^#[0-9a-f]{6}$/i.test(color)) return color;
    }
    for (const name of names) {
        const fallback = TOKEN_CONTRACT_DEFAULTS[name];
        if (fallback) return hslChannelsToHex(fallback)!;
    }
    return hslChannelsToHex(TOKEN_CONTRACT_DEFAULTS['--foreground'])!;
}

/**
 * The text colour that reads best on `fill`: the contract's dark ink (`--foreground`) or light ink
 * (`--primary-foreground`). Contract defaults on purpose: a preset's own foreground flips with the colour mode,
 * while text on a filled shape depends only on the fill.
 */
export function inkOnFill(fill: string): string {
    const dark = hslChannelsToHex(TOKEN_CONTRACT_DEFAULTS['--foreground'])!;
    const light = hslChannelsToHex(TOKEN_CONTRACT_DEFAULTS['--primary-foreground'])!;
    return contrastRatio(fill, light) >= contrastRatio(fill, dark) ? light : dark;
}

/** Read the theme from tokens, guaranteeing readable text even when a preset lacks dark tokens. */
export function readChartTheme(): ChartTheme {
    // The surface is what the card actually paints; text colours are then checked against it, so a colour
    // mode whose preset lacks dark tokens still gets readable text on whatever surface is really there.
    const surface = themeColor('--card', '--background');
    const darkSurface = luminance(surface) < 0.2;
    // A token that fails contrast on this surface falls back to the contract ink that reads best on it.
    const ink = inkOnFill(surface);
    const foreColor = readable(themeColor('--foreground'), surface, ink);
    const mutedColor = readable(themeColor('--muted-foreground'), surface, mutedInk(ink, surface));
    let grid = themeColor('--border');
    if (contrastRatio(grid, surface) < 1.15) grid = mixHex(foreColor, surface, darkSurface ? 0.75 : 0.85);
    const fontFamily = rootStyle()?.fontFamily || 'inherit';
    return { mode: darkSurface ? 'dark' : 'light', foreColor, mutedColor, grid, surface, fontFamily };
}

function readable(color: string, surface: string, fallback: string): string {
    return contrastRatio(color, surface) >= 4.5 ? color : fallback;
}

/** Secondary text: the ink eased toward the surface, as far as it can go while staying at 4.5:1. */
function mutedInk(ink: string, surface: string): string {
    for (const amount of [0.35, 0.25, 0.15]) {
        const muted = mixHex(ink, surface, amount);
        if (contrastRatio(muted, surface) >= 4.5) return muted;
    }
    return ink;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let observer: MutationObserver | null = null;
let media: MediaQueryList | null = null;
let pending = 0;
let lastSignature = '';

function themeSignature(): string {
    return [colorMode(), ...['--card', '--background', '--foreground', '--muted-foreground', '--border', '--primary',
        '--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6', '--chart-7', '--chart-8'].map(cssToken)].join('|');
}

function notify(): void {
    if (pending) return;
    // Coalesce attribute bursts (preset + mode switched together) into one redraw.
    pending = (typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number)(() => {
        pending = 0;
        // Unrelated class changes on <html> (scroll locks, focus modes) must not redraw every chart.
        const signature = themeSignature();
        if (signature === lastSignature) return;
        lastSignature = signature; resolvedColors.clear();
        for (const listener of [...listeners]) { try { listener(); } catch (error) { console.warn('analytics-charts: theme redraw failed', error); } }
    });
}
const onMedia = () => notify();

/** Call `listener` after the colour mode, preset or design system changes. Returns an unsubscribe. */
export function onThemeChange(listener: Listener): () => void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => {};
    listeners.add(listener);
    if (!observer) {
        lastSignature = themeSignature();
        observer = new MutationObserver(notify);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-color-mode', 'data-preset', 'data-design-system', 'data-theme', 'class'] });
        try { media = matchMedia('(prefers-color-scheme: dark)'); media.addEventListener?.('change', onMedia); } catch { media = null; }
    }
    return () => {
        listeners.delete(listener);
        if (!listeners.size && observer) {
            observer.disconnect(); observer = null;
            media?.removeEventListener?.('change', onMedia); media = null;
        }
    };
}
